import type React from "react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import {
  Box,
  Flex,
  Heading,
  Text,
  Icon,
  Collapse,
  useDisclosure,
  Divider,
  Stack,
  useColorModeValue,
  Button,
} from "@chakra-ui/react";
import { theme } from "src/styles/theme";
import { FiAlertTriangle } from "react-icons/fi";

interface ErrorDetailsProps {
  error: Error | null;
}

// Componente para exibir detalhes do erro (extraído para melhor organização)
const ErrorDetails: React.FC<ErrorDetailsProps> = ({ error }) => {
  const { isOpen, onToggle } = useDisclosure();
  const bgColor = useColorModeValue(theme.disable100, "gray.700");
  const textColor = useColorModeValue("gray.800", "gray.100");

  if (!error) return null;

  return (
    <Box mt={4} mb={6}>
      <Box h={"30px"}>
        <Button
          onClick={onToggle}
          _hover={{ opacity: 1 }}
          border={`2px solid ${theme.error}`}
          h={"36px"}
          borderRadius={"8px"}
          fontSize={"14px"}
          alignItems={"center"}
          justifyContent={"center"}
          backgroundColor={`${theme.error}`}
          color={"white"}
          opacity={0.8}
        >
          {isOpen ? "Ocultar detalhes técnicos" : "Mostrar detalhes técnicos"}
        </Button>
      </Box>
      <Collapse in={isOpen} animateOpacity>
        <Box
          p={4}
          mt={2}
          bg={bgColor}
          borderRadius="md"
          textAlign="left"
          overflowX="auto"
          borderWidth="1px"
          borderColor="gray.200"
        >
          <Text fontFamily="monospace" fontSize="sm" color={textColor}>
            {error.toString()}
          </Text>

          {error.stack && (
            <>
              <Divider my={2} />
              <Text
                fontFamily="monospace"
                fontSize="xs"
                color={textColor}
                whiteSpace="pre-wrap"
              >
                {error.stack.split(error.toString())[1]}
              </Text>
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnChange?: any[];
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Registrar o erro
    console.error("Error caught by ErrorBoundary:", error, errorInfo);

    // Chamar o callback onError se fornecido
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props): void {
    // Resetar o estado de erro se as dependências mudarem
    if (
      this.state.hasError &&
      this.props.resetOnChange &&
      prevProps.resetOnChange &&
      this.props.resetOnChange.some(
        (value, index) => value !== prevProps.resetOnChange![index]
      )
    ) {
      this.resetErrorBoundary();
    }
  }

  resetErrorBoundary = (): void => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Renderizar o fallback personalizado se fornecido
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Renderizar o fallback padrão melhorado
      return (
        <Flex
          display={"flex"}
          pos={"relative"}
          flexDir={"column"}
          p={8}
          left={{ lg: "230px" }}
          mt={{ base: "20px", md: "15px", lg: "0px" }}
          w={
            window.location.href.includes("login") ||
            window.location.href.includes("needHelp")
              ? "99%"
              : { lg: "calc(100% - 230px)" }
          }
          overflowX={
            window.location.href.includes("login") ||
            window.location.href.includes("needHelp")
              ? "hidden"
              : "auto"
          }
          bg={
            window.location.href.includes("login") ||
            window.location.href.includes("needHelp")
              ? "white"
              : theme.disable100
          }
          h={
            window.location.href.includes("login") ||
            window.location.href.includes("needHelp")
              ? "90vh"
              : "100%"
          }
        >
          <Box
            p={8}
            borderRadius="xl"
            shadow="lg"
            w="100%"
            textAlign="center"
            borderWidth="1px"
            borderColor="gray.100"
            transition="all 0.3s"
          >
            <Flex direction="column" align="center" mb={6}>
              <Icon
                as={FiAlertTriangle}
                color={theme.error}
                boxSize={16}
                mb={4}
                animation="pulse 2s infinite"
                sx={{
                  "@keyframes pulse": {
                    "0%": { opacity: 0.8, transform: "scale(0.95)" },
                    "70%": { opacity: 1, transform: "scale(1.05)" },
                    "100%": { opacity: 0.8, transform: "scale(0.95)" },
                  },
                }}
              />

              <Heading as="h2" size="xl" mb={2} color={theme.error}>
                Ops! Algo deu errado
              </Heading>

              <Text fontSize="md" color="gray.500">
                Encontramos um problema inesperado
              </Text>
            </Flex>

            <Divider mb={6} />

            <Text mb={6} color={theme.text} fontSize="lg">
              Nossa equipe foi notificada e está trabalhando para resolver o
              problema. Enquanto isso, você pode tentar uma das seguintes
              opções:
            </Text>

            <ErrorDetails error={this.state.error} />

            <Stack
              direction={{ base: "column", md: "row" }}
              spacing={4}
              justify="center"
              mt={8}
            >
              <Button
                onClick={this.resetErrorBoundary}
                _hover={{ opacity: 1 }}
                border={`2px solid ${theme.primary}`}
                h={"36px"}
                borderRadius={"8px"}
                fontSize={"14px"}
                alignItems={"center"}
                justifyContent={"center"}
                backgroundColor={`white`}
                color={`${theme.primary}`}
                opacity={0.8}
              >
                Tentar novamente
              </Button>

              <Button
                onClick={() => {
                  window.location.href = "/Home";
                  localStorage.setItem("@option", "Home");
                }}
                _hover={{ opacity: 1 }}
                border={`none`}
                h={"36px"}
                borderRadius={"8px"}
                fontSize={"14px"}
                alignItems={"center"}
                justifyContent={"center"}
                backgroundColor={`${theme.primary}`}
                color={`white`}
                opacity={0.8}
              >
                Página inicial
              </Button>
            </Stack>

            <Text fontSize="sm" color="gray.500" mt={8}>
              Se o problema persistir, entre em contato com o suporte técnico.
            </Text>
          </Box>
        </Flex>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
